import { useState, useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

export default function ServicesPage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    document.title = 'Home Automation Services Florida | Control4 Savant RTI | RDM Security';
    
    // Handle hash navigation on page load
    const hash = window.location.hash;
    if (hash) {
      setTimeout(() => {
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const services = [
    {
      icon: 'ri-home-smile-line',
      title: 'Home Automation',
      description: 'Complete smart home integration with Control4, Savant, RTI, and Brilliant systems. Control lighting, climate, entertainment, and security from one unified interface.',
      features: ['Lighting Control', 'Climate Management', 'Audio/Video Integration', 'Voice Control', 'Mobile App Access']
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Security Systems',
      description: 'Advanced security camera installation and monitoring systems. Protect your property with high-definition surveillance, motion detection, and remote viewing capabilities.',
      features: ['4K Security Cameras', 'Motion Detection', 'Night Vision', 'Remote Monitoring', 'Cloud Storage']
    },
    {
      icon: 'ri-wifi-line',
      title: 'Network Infrastructure',
      description: 'Professional fiber optic and structured cabling installation. Build a robust network foundation for seamless connectivity throughout your property.',
      features: ['Fiber Optic Installation', 'Cat6/Cat6a Cabling', 'Network Design', 'WiFi Optimization', 'Enterprise-Grade Equipment']
    },
    {
      icon: 'ri-tv-line',
      title: 'Audio/Video Solutions',
      description: 'Premium multi-room audio and home theater systems. Experience cinema-quality entertainment with distributed audio and 4K video distribution.',
      features: ['Home Theater Design', 'Multi-Room Audio', 'Video Distribution', 'Acoustic Treatment', 'Custom Integration']
    },
    {
      icon: 'ri-lightbulb-line',
      title: 'Lighting Design',
      description: 'Intelligent lighting control systems that adapt to your lifestyle. Create the perfect ambiance with automated scenes and energy-efficient solutions.',
      features: ['Automated Scenes', 'Dimming Control', 'Color Temperature', 'Energy Monitoring', 'Outdoor Lighting']
    },
    {
      icon: 'ri-settings-3-line',
      title: 'System Maintenance',
      description: 'Ongoing support and maintenance for your automation systems. Keep your technology running smoothly with regular updates and expert troubleshooting.',
      features: ['Software Updates', 'System Optimization', 'Remote Support', 'Annual Inspections', 'Priority Service']
    }
  ];

  const process = [
    {
      number: '01',
      title: 'Consultation',
      description: 'We start with an in-depth consultation to understand your needs, lifestyle, and vision for your smart home.'
    },
    {
      number: '02',
      title: 'Design',
      description: 'Our team creates a custom design plan tailored to your property, incorporating the best technology solutions.'
    },
    {
      number: '03',
      title: 'Installation',
      description: 'Professional installation by certified technicians ensures every component is perfectly integrated and functioning.'
    },
    {
      number: '04',
      title: 'Training',
      description: 'Comprehensive training on your new systems so you can confidently control every aspect of your smart home.'
    },
    {
      number: '05',
      title: 'Support',
      description: 'Ongoing support and maintenance to keep your systems running flawlessly for years to come.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar scrolled={scrolled} />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-gray-900 via-gray-800 to-black overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/40"></div>
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: 'url(https://public.readdy.ai/ai/img_res/7e439b23072d8734fe53da2967887589.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        
        <div className="relative max-w-[1400px] mx-auto px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Our <span className="text-[#4A90E2]">Services</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Comprehensive home automation and security solutions tailored to your lifestyle. From initial consultation to ongoing support, we deliver excellence at every step.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Control4 */}
            <div id="control4" className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-xl transition-shadow scroll-mt-24">
              <div className="w-16 h-16 bg-gradient-to-br from-[#4A90E2] to-[#3A7BC8] rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-home-smile-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Home Automation</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">Complete smart home integration with Control4, Savant, RTI, and Brilliant systems. Control lighting, climate, entertainment, and security from one unified interface.</p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-[#4A90E2]"></i>
                  Lighting Control
                </li>
                <li className="flex items-center gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-[#4A90E2]"></i>
                  Climate Management
                </li>
                <li className="flex items-center gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-[#4A90E2]"></i>
                  Audio/Video Integration
                </li>
                <li className="flex items-center gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-[#4A90E2]"></i>
                  Voice Control
                </li>
                <li className="flex items-center gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-[#4A90E2]"></i>
                  Mobile App Access
                </li>
              </ul>
            </div>

            {/* Savant */}
            <div id="savant" className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-xl transition-shadow scroll-mt-24">
              <div className="w-16 h-16 bg-gradient-to-br from-[#4A90E2] to-[#3A7BC8] rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-home-smile-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Home Automation</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">Complete smart home integration with Control4, Savant, RTI, and Brilliant systems. Control lighting, climate, entertainment, and security from one unified interface.</p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-[#4A90E2]"></i>
                  Lighting Control
                </li>
                <li className="flex items-center gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-[#4A90E2]"></i>
                  Climate Management
                </li>
                <li className="flex items-center gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-[#4A90E2]"></i>
                  Audio/Video Integration
                </li>
                <li className="flex items-center gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-[#4A90E2]"></i>
                  Voice Control
                </li>
                <li className="flex items-center gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-[#4A90E2]"></i>
                  Mobile App Access
                </li>
              </ul>
            </div>

            {/* RTI */}
            <div id="rti" className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-xl transition-shadow scroll-mt-24">
              <div className="w-16 h-16 bg-gradient-to-br from-[#4A90E2] to-[#3A7BC8] rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-home-smile-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Home Automation</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">Complete smart home integration with Control4, Savant, RTI, and Brilliant systems. Control lighting, climate, entertainment, and security from one unified interface.</p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-[#4A90E2]"></i>
                  Lighting Control
                </li>
                <li className="flex items-center gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-[#4A90E2]"></i>
                  Climate Management
                </li>
                <li className="flex items-center gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-[#4A90E2]"></i>
                  Audio/Video Integration
                </li>
                <li className="flex items-center gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-[#4A90E2]"></i>
                  Voice Control
                </li>
                <li className="flex items-center gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-[#4A90E2]"></i>
                  Mobile App Access
                </li>
              </ul>
            </div>

            {/* Brilliant */}
            <div id="brilliant" className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-xl transition-shadow scroll-mt-24">
              <div className="w-16 h-16 bg-gradient-to-br from-[#4A90E2] to-[#3A7BC8] rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-home-smile-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Home Automation</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">Complete smart home integration with Control4, Savant, RTI, and Brilliant systems. Control lighting, climate, entertainment, and security from one unified interface.</p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-[#4A90E2]"></i>
                  Lighting Control
                </li>
                <li className="flex items-center gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-[#4A90E2]"></i>
                  Climate Management
                </li>
                <li className="flex items-center gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-[#4A90E2]"></i>
                  Audio/Video Integration
                </li>
                <li className="flex items-center gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-[#4A90E2]"></i>
                  Voice Control
                </li>
                <li className="flex items-center gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-[#4A90E2]"></i>
                  Mobile App Access
                </li>
              </ul>
            </div>

            {/* Fiber Optic */}
            <div id="fiber" className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-xl transition-shadow scroll-mt-24">
              <div className="w-16 h-16 bg-gradient-to-br from-[#4A90E2] to-[#3A7BC8] rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-wifi-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Network Infrastructure</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">Professional fiber optic and structured cabling installation. Build a robust network foundation for seamless connectivity throughout your property.</p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-[#4A90E2]"></i>
                  Fiber Optic Installation
                </li>
                <li className="flex items-center gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-[#4A90E2]"></i>
                  Cat6/Cat6a Cabling
                </li>
                <li className="flex items-center gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-[#4A90E2]"></i>
                  Network Design
                </li>
                <li className="flex items-center gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-[#4A90E2]"></i>
                  WiFi Optimization
                </li>
                <li className="flex items-center gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-[#4A90E2]"></i>
                  Enterprise-Grade Equipment
                </li>
              </ul>
            </div>

            {/* Security Cameras */}
            <div id="security" className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-xl transition-shadow scroll-mt-24">
              <div className="w-16 h-16 bg-gradient-to-br from-[#4A90E2] to-[#3A7BC8] rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-shield-check-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Security Systems</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">Advanced security camera installation and monitoring systems. Protect your property with high-definition surveillance, motion detection, and remote viewing capabilities.</p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-[#4A90E2]"></i>
                  4K Security Cameras
                </li>
                <li className="flex items-center gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-[#4A90E2]"></i>
                  Motion Detection
                </li>
                <li className="flex items-center gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-[#4A90E2]"></i>
                  Night Vision
                </li>
                <li className="flex items-center gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-[#4A90E2]"></i>
                  Remote Monitoring
                </li>
                <li className="flex items-center gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-[#4A90E2]"></i>
                  Cloud Storage
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-[1400px] mx-auto px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our <span className="text-[#4A90E2]">Process</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A proven methodology that ensures your project is completed on time, on budget, and exceeds expectations.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
            {process.map((step, index) => (
              <div key={index} className="relative">
                <div className="text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-[#4A90E2] to-[#3A7BC8] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-[#4A90E2]/30">
                    <span className="text-2xl font-bold text-white">{step.number}</span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{step.description}</p>
                </div>
                {index < process.length - 1 && (
                  <div className="hidden md:block absolute top-10 left-[60%] w-[80%] h-0.5 bg-gradient-to-r from-[#4A90E2] to-transparent"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white">
        <div className="max-w-[1400px] mx-auto px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Transform Your Home?
          </h2>
          <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto">
            Schedule a consultation with our experts and discover how we can elevate your living experience.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/#schedule"
              className="px-8 py-4 bg-[#4A90E2] text-white text-base font-semibold rounded-full hover:bg-[#3A7BC8] hover:shadow-lg hover:shadow-[#4A90E2]/30 transition-all flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
            >
              Schedule Survey
              <i className="ri-arrow-right-line"></i>
            </a>
            <a
              href="/#contact"
              className="px-8 py-4 bg-white/10 text-white text-base font-semibold rounded-full hover:bg-white/20 border border-white/20 transition-all flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
            >
              Contact Us
              <i className="ri-phone-line"></i>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
